import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { ApiResult, HandledError } from '@synergy/api';
import { Dispatch } from 'redux';
import { FormikErrors } from 'formik';
import { Media } from '@synergy/models';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';

import { Api } from '../../../../../../api';
import { AppState } from '../../../../../../store/models/app-state';
import { BankAccount } from '../models/bank-accounts';
import { BankAccountErrorCode } from '../../../../models/bank-account-error-code';
import { BankAccountFormValues } from '../../../../models/bank-account-form-values';
import { ContactDetailOverviewFormValues } from '../models/contact-detail-overview-form-values';
import { EmailErrorCode } from '../../../../models/email-error-code';
import { EmbeddedContact } from '../../../../../bonus-settlements/models/embedded-contact';
import { RelatedAccount } from '../models/related-account';
import { SaveResult } from '../../../../../../common/models/save-result';
import { contactDetailOverviewRequestSelector } from './contact-detail-overview-selectors';

export const CONTACT_DETAIL_BANK_ACCOUNT_ADD = 'CONTACT_DETAIL_BANK_ACCOUNT_ADD';
export const CONTACT_DETAIL_BANK_ACCOUNT_CREATED = 'CONTACT_DETAIL_BANK_ACCOUNT_CREATED';
export const CONTACT_DETAIL_BANK_ACCOUNT_DELETE = 'CONTACT_DETAIL_BANK_ACCOUNT_DELETE';
export const CONTACT_DETAIL_BANK_ACCOUNT_DELETE_FINISHED = 'CONTACT_DETAIL_BANK_ACCOUNT_DELETE_FINISHED';
export const CONTACT_DETAIL_BANK_ACCOUNT_UPDATED = 'CONTACT_DETAIL_BANK_ACCOUNT_UPDATED';
export const CONTACT_DETAIL_BANK_ACCOUNT_UPLOAD_FILE = 'CONTACT_DETAIL_BANK_ACCOUNT_UPLOAD_FILE';
export const CONTACT_DETAIL_BANK_ACCOUNT_UPLOAD_FILE_FINISHED = 'CONTACT_DETAIL_BANK_ACCOUNT_UPLOAD_FILE_FINISHED';
export const CONTACT_DETAIL_OVERVIEW_ERROR = 'CONTACT_DETAIL_OVERVIEW_ERROR';
export const CONTACT_DETAIL_BANK_ACCOUNT_ERROR = 'CONTACT_DETAIL_BANK_ACCOUNT_ERROR';
export const CONTACT_DETAIL_OVERVIEW_RECEIVE_DETAILS = 'CONTACT_DETAIL_OVERVIEW_RECEIVE_DETAILS';
export const CONTACT_DETAIL_OVERVIEW_REQUEST_DETAILS = 'CONTACT_DETAIL_OVERVIEW_REQUEST_DETAILS';
export const CONTACT_DETAIL_OVERVIEW_SAVE = 'CONTACT_DETAIL_OVERVIEW_SAVE';
export const CONTACT_DETAIL_OVERVIEW_UPDATE = 'CONTACT_DETAIL_OVERVIEW_UPDATE';
export const CONTACT_DETAIL_OVERVIEW_PROFILE_IMAGE_UPLOAD_FINISHED =
    'CONTACT_DETAIL_OVERVIEW_PROFILE_IMAGE_UPLOAD_FINISHED';
export const CONTACT_DETAIL_OVERVIEW_PROFILE_IMAGE_UPLOAD = 'CONTACT_DETAIL_OVERVIEW_PROFILE_IMAGE_UPLOAD';
export const CONTACT_DETAIL_OVERVIEW_PROFILE_IMAGE_REMOVED = 'CONTACT_DETAIL_OVERVIEW_PROFILE_IMAGE_REMOVED';
export const CONTACT_DETAIL_OVERVIEW_RELATED_ACCOUNTS_GET = 'CONTACT_DETAIL_OVERVIEW_RELATED_ACCOUNTS_GET';
export const CONTACT_DETAIL_OVERVIEW_RELATED_ACCOUNTS_RECEIVED = 'CONTACT_DETAIL_OVERVIEW_RELATED_ACCOUNTS_RECEIVED';

const requestContact = () => {
    return {
        type: CONTACT_DETAIL_OVERVIEW_REQUEST_DETAILS,
    };
};

export const updateContactDetail = (
    formData: ContactDetailOverviewFormValues,
    isDirty: boolean,
    isValid: boolean,
    errors: FormikErrors<ContactDetailOverviewFormValues>
) => {
    return {
        type: CONTACT_DETAIL_OVERVIEW_UPDATE,
        formData,
        isDirty,
        isValid,
        errors,
    };
};

const overviewError = (errorData?: HandledError<EmailErrorCode>[]) => ({
    type: CONTACT_DETAIL_OVERVIEW_ERROR,
    errorData,
});

const bankAccountError = (errorData?: HandledError<BankAccountErrorCode>[]) => ({
    type: CONTACT_DETAIL_BANK_ACCOUNT_ERROR,
    errorData,
});

const receiveContactDetails = (contact: EmbeddedContact) => {
    return {
        type: CONTACT_DETAIL_OVERVIEW_RECEIVE_DETAILS,
        contact,
    };
};

const receiveRelatedAccounts = (relatedAccounts: RelatedAccount[]) => {
    return {
        type: CONTACT_DETAIL_OVERVIEW_RELATED_ACCOUNTS_RECEIVED,
        relatedAccounts,
    };
};

const receiveNewBankAccount = (newBankAccount: BankAccount) => {
    return {
        type: CONTACT_DETAIL_BANK_ACCOUNT_CREATED,
        newBankAccount,
    };
};

const receiveUpdatedBankAccount = (bankAccount: BankAccount) => {
    return {
        type: CONTACT_DETAIL_BANK_ACCOUNT_UPDATED,
        bankAccount,
    };
};

const deleteBankAccountFinished = (id: number) => {
    return {
        type: CONTACT_DETAIL_BANK_ACCOUNT_DELETE_FINISHED,
        id,
    };
};

const fileUploadFinished = (bankAccountId: number, media: Media) => {
    return {
        type: CONTACT_DETAIL_BANK_ACCOUNT_UPLOAD_FILE_FINISHED,
        bankAccountId,
        media,
    };
};

const profileUploadFinished = (media: Media) => {
    return {
        type: CONTACT_DETAIL_OVERVIEW_PROFILE_IMAGE_UPLOAD_FINISHED,
        media,
    };
};

const uploadNewFiles = (media: Media[], id: number) => {
    return (dispatch: Dispatch, getState: () => AppState, api: { api: Api }) => {
        const newFiles = media.reduce(
            (acc: File[], item: Media) => (item.file !== undefined ? [...acc, item.file] : acc),
            []
        );

        if (newFiles) {
            newFiles.forEach(file => {
                uploadFile(id, file)(dispatch, getState, api);
            });
        }
    };
};

export const fetchContactDetails = (contactId: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(requestContact());
        const response = await api.contactsAndOrganizations.Contact.ContactDetail.Overview.get(contactId);
        if (response.success) {
            dispatch(receiveContactDetails(response.result));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const saveContactDetail = () => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: CONTACT_DETAIL_OVERVIEW_SAVE });
        const request = contactDetailOverviewRequestSelector(getState());
        if (!request) {
            return SaveResult.Error;
        }
        const contactId = getState().contactsAndOrganizations.contacts.contactDetail.overview.contact?.id;

        let response: ApiResult<EmbeddedContact>;
        if (contactId) {
            response = await api.contactsAndOrganizations.Contact.ContactDetail.Overview.patchDetail(
                contactId,
                request
            );
        } else {
            response = await api.contactsAndOrganizations.Contact.ContactDetail.Overview.createDetail(request);
        }

        if (response.success) {
            dispatch(receiveContactDetails(response.result));
            return SaveResult.Success;
        }

        if (response.errorHandled) {
            dispatch(overviewError(response.handledErrors));
            return SaveResult.FormErrors;
        }
        return SaveResult.Error;
    };
};

export const addNewBankAccount = (bankAccount: BankAccountFormValues) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: CONTACT_DETAIL_BANK_ACCOUNT_ADD });

        const contactId = getState().contactsAndOrganizations.contacts.contactDetail.overview.contact?.id;
        if (!contactId) {
            return;
        }

        const response = await api.contactsAndOrganizations.Contact.ContactDetail.Overview.addBankAccount(
            contactId,
            bankAccount
        );

        if (response.success) {
            dispatch(receiveNewBankAccount(response.result));
            uploadNewFiles(bankAccount.media, response.result.id)(dispatch, getState, { api });
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.bankAccountAdded')));
        } else {
            if (response.errorHandled) {
                dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.bankAccountValidationFailed')));
                dispatch(bankAccountError(response.handledErrors));
            } else {
                dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.bankAccountError')));
            }
        }

        return response;
    };
};

export const updateExistingBankAccount = (bankAccount: BankAccountFormValues) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const contactId = getState().contactsAndOrganizations.contacts.contactDetail.overview.contact?.id;
        const id = bankAccount.id;
        if (!contactId || !id) {
            return;
        }

        bankAccount.media.filter(media => media.id > 0);
        delete bankAccount.id;
        const response = await api.contactsAndOrganizations.Contact.ContactDetail.Overview.updateBankAccount(
            contactId,
            bankAccount,
            id
        );

        if (response.success) {
            dispatch(receiveUpdatedBankAccount(response.result));
            uploadNewFiles(bankAccount.media, response.result.id)(dispatch, getState, { api });
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.bankAccountSaved')));
        } else {
            if (response.errorHandled) {
                dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.bankAccountValidationFailed')));
                dispatch(bankAccountError(response.handledErrors));
            } else {
                dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.bankAccountError')));
            }
        }

        return response;
    };
};

export const deleteBankAccount = (id: number) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: CONTACT_DETAIL_BANK_ACCOUNT_DELETE });

        const contactId = getState().contactsAndOrganizations.contacts.contactDetail.overview.contact?.id;
        if (!contactId) {
            return;
        }

        const response = await api.contactsAndOrganizations.Contact.ContactDetail.Overview.deleteBankAccount(
            contactId,
            id
        );

        if (response.success) {
            dispatch(deleteBankAccountFinished(id));
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.bankAccountDeleteSuccess')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const uploadFile = (id: number, file: File) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: CONTACT_DETAIL_BANK_ACCOUNT_UPLOAD_FILE });

        const contactId = getState().contactsAndOrganizations.contacts.contactDetail.overview.contact?.id;
        if (!contactId) {
            return;
        }

        const response = await api.contactsAndOrganizations.Contact.ContactDetail.Overview.uploadFile(
            contactId,
            id,
            file
        );

        if (response.success) {
            dispatch(fileUploadFinished(id, response.result));
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.fileUploadSuccess')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.fileUploadError')));
        }
    };
};

export const uploadProfileImage = (file: File) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: CONTACT_DETAIL_OVERVIEW_PROFILE_IMAGE_UPLOAD });

        const contactId = getState().contactsAndOrganizations.contacts.contactDetail.overview.contact?.id;
        if (!contactId) {
            return;
        }

        const response = await api.contactsAndOrganizations.Contact.ContactDetail.Overview.uploadProfileImage(
            contactId,
            file
        );

        if (response.success) {
            dispatch(profileUploadFinished(response.result));
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.avatarUploadSuccess')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.avatarUploadError')));
        }
    };
};

export const removeProfileImage = () => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: CONTACT_DETAIL_OVERVIEW_PROFILE_IMAGE_REMOVED });
        const contactId = getState().contactsAndOrganizations.contacts.contactDetail.overview.contact?.id;
        if (!contactId) {
            return;
        }

        const response = await api.contactsAndOrganizations.Contact.ContactDetail.Overview.removeProfileImage(
            contactId
        );

        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.avatarRemoveSuccess')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.avatarRemoveError')));
        }
    };
};

export const fetchRelatedAccounts = () => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        dispatch({ type: CONTACT_DETAIL_OVERVIEW_RELATED_ACCOUNTS_GET });
        const contactId = getState().contactsAndOrganizations.contacts.contactDetail.overview.contact?.id;
        if (!contactId) {
            return;
        }

        const response = await api.contactsAndOrganizations.Contact.ContactDetail.Overview.getRelatedAccounts(
            contactId
        );

        if (response.success) {
            dispatch(receiveRelatedAccounts(response.result));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};
